import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Sun,
    Moon,
    Sparkles,
    CheckCircle,
    AlertTriangle,
    Calendar,
    Target,
    Coffee,
    ChevronRight,
    Volume2,
} from 'lucide-react';
import { useAppState } from '../../contexts/AppContext';

interface BriefingSection {
    id: string;
    title: string;
    icon: React.ElementType;
    content: string[];
    priority?: 'high' | 'medium' | 'low';
}

export default function AppMorningBriefing() {
    const navigate = useNavigate();
    const { tasks, events, projects } = useAppState();
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    // Update time every minute
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    // Generate personalized briefing content
    const briefing = useMemo(() => {
        const today = currentTime.toISOString().split('T')[0];
        const hour = currentTime.getHours();

        // Time-based greeting
        let greeting = 'Good morning';
        let greetingIcon = Sun;
        if (hour >= 12 && hour < 17) {
            greeting = 'Good afternoon';
            greetingIcon = Coffee;
        } else if (hour >= 17) {
            greeting = 'Good evening';
            greetingIcon = Moon;
        }

        // Get today's data
        const todayTasks = tasks.filter(t =>
            !t.deletedAt &&
            (t.dueDate === today || (!t.dueDate && !t.completed))
        );
        const incompleteTasks = todayTasks.filter(t => !t.completed);
        const completedTasks = todayTasks.filter(t => t.completed);
        const highPriorityTasks = incompleteTasks.filter(t => t.priority === 'high');

        const todayEvents = events.filter(e =>
            e.date === today && !e.deletedAt
        ).sort((a, b) => a.timeStart.localeCompare(b.timeStart));

        // Overdue tasks
        const overdueTasks = tasks.filter(t =>
            !t.completed &&
            t.dueDate &&
            t.dueDate < today &&
            !t.deletedAt
        );

        // Build sections
        const sections: BriefingSection[] = [];

        // Today's Focus
        const focusItems: string[] = [];
        if (highPriorityTasks.length > 0) {
            focusItems.push(`You have ${highPriorityTasks.length} high-priority task${highPriorityTasks.length > 1 ? 's' : ''} to focus on today`);
            highPriorityTasks.slice(0, 3).forEach(t => {
                focusItems.push(`• ${t.title}`);
            });
        } else if (incompleteTasks.length > 0) {
            focusItems.push(`${incompleteTasks.length} task${incompleteTasks.length > 1 ? 's' : ''} waiting for your attention`);
        } else {
            focusItems.push("No tasks scheduled - a perfect day to plan ahead!");
        }

        sections.push({
            id: 'focus',
            title: "Today's Focus",
            icon: Target,
            content: focusItems,
            priority: highPriorityTasks.length > 0 ? 'high' : 'low',
        });

        // Calendar Overview
        const calendarItems: string[] = [];
        if (todayEvents.length === 0) {
            calendarItems.push("Your calendar is clear today - great for deep work!");
        } else {
            calendarItems.push(`${todayEvents.length} event${todayEvents.length > 1 ? 's' : ''} on your calendar`);
            todayEvents.slice(0, 3).forEach(e => {
                calendarItems.push(`• ${e.timeStart} - ${e.title}`);
            });
        }

        sections.push({
            id: 'calendar',
            title: 'Calendar',
            icon: Calendar,
            content: calendarItems,
        });

        // Alerts & Overdue
        if (overdueTasks.length > 0) {
            sections.push({
                id: 'alerts',
                title: 'Needs Attention',
                icon: AlertTriangle,
                content: [
                    `${overdueTasks.length} overdue task${overdueTasks.length > 1 ? 's' : ''}`,
                    ...overdueTasks.slice(0, 3).map(t => `• ${t.title} (due ${t.dueDate})`),
                ],
                priority: 'high',
            });
        }

        // Progress So Far
        if (completedTasks.length > 0) {
            sections.push({
                id: 'progress',
                title: 'Already Done',
                icon: CheckCircle,
                content: [
                    `Great job! You've completed ${completedTasks.length} task${completedTasks.length > 1 ? 's' : ''} today`,
                ],
            });
        }

        // Productivity Tips
        const tips: string[] = [];
        if (hour < 12) {
            tips.push("Your peak energy is typically in the morning - tackle the hard stuff first!");
        }
        if (incompleteTasks.length > 5) {
            tips.push("Consider batching similar tasks together for better flow");
        }
        if (todayEvents.length > 3) {
            tips.push("Heavy meeting day - protect some focus time between calls");
        }
        if (tips.length === 0) {
            tips.push("Stay hydrated and take short breaks for sustained productivity");
        }

        sections.push({
            id: 'tips',
            title: 'Pro Tip',
            icon: Sparkles,
            content: tips,
        });

        return {
            greeting,
            greetingIcon,
            sections,
            stats: {
                totalTasks: incompleteTasks.length,
                highPriority: highPriorityTasks.length,
                events: todayEvents.length,
                overdue: overdueTasks.length,
            },
        };
    }, [tasks, events, projects, currentTime]);

    const handleSpeak = () => {
        if ('speechSynthesis' in window) {
            if (isPlaying) {
                window.speechSynthesis.cancel();
                setIsPlaying(false);
                return;
            }

            const allText = [
                `${briefing.greeting}!`,
                ...briefing.sections.flatMap(s => [s.title, ...s.content]),
            ].join('. ');

            const utterance = new SpeechSynthesisUtterance(allText);
            utterance.onend = () => setIsPlaying(false);

            setIsPlaying(true);
            window.speechSynthesis.speak(utterance);
        }
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
        });
    };

    const GreetingIcon = briefing.greetingIcon;

    return (
        <div className="min-h-screen bg-[#F5F8FA] dark:bg-dark-bg pb-24">
            {/* Header with gradient */}
            <div className="bg-gradient-to-br from-[#0D5F6B] to-[#2A7A85] text-white px-4 pt-6 pb-8">
                <div className="flex items-center justify-between mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-white/10 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleSpeak}
                        className={`p-2 rounded-full transition-colors ${isPlaying ? 'bg-white text-[#0D5F6B]' : 'hover:bg-white/10'
                            }`}
                    >
                        <Volume2 className="w-5 h-5" />
                    </button>
                </div>

                {/* Greeting */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <GreetingIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">{briefing.greeting}!</h1>
                        <p className="text-white/80 text-sm">{formatDate(currentTime)}</p>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-2 mt-4">
                    <StatBadge
                        value={briefing.stats.totalTasks}
                        label="Tasks"
                        color={briefing.stats.totalTasks > 0 ? 'white' : 'green'}
                    />
                    <StatBadge
                        value={briefing.stats.highPriority}
                        label="High Pri"
                        color={briefing.stats.highPriority > 0 ? 'amber' : 'green'}
                    />
                    <StatBadge
                        value={briefing.stats.events}
                        label="Events"
                        color="white"
                    />
                    <StatBadge
                        value={briefing.stats.overdue}
                        label="Overdue"
                        color={briefing.stats.overdue > 0 ? 'red' : 'green'}
                    />
                </div>
            </div>

            {/* Content Sections */}
            <div className="p-4 space-y-4 -mt-4">
                {briefing.sections.map(section => {
                    const SectionIcon = section.icon;
                    const isAlert = section.priority === 'high';

                    return (
                        <div
                            key={section.id}
                            className={`rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)] dark:shadow-dark ${isAlert
                                ? 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30'
                                : 'bg-white dark:bg-dark-card'
                                }`}
                        >
                            <div className="flex items-center gap-2 mb-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isAlert
                                    ? 'bg-red-100 dark:bg-red-900/30'
                                    : 'bg-[#D9EEF2] dark:bg-dark-elevated'
                                    }`}>
                                    <SectionIcon className={`w-4 h-4 ${isAlert ? 'text-red-500' : 'text-[#0D5F6B]'
                                        }`} />
                                </div>
                                <h3 className={`font-semibold ${isAlert ? 'text-red-700 dark:text-red-400' : 'text-[#1A2832] dark:text-white'
                                    }`}>
                                    {section.title}
                                </h3>
                            </div>
                            <div className="space-y-1">
                                {section.content.map((line, i) => (
                                    <p
                                        key={i}
                                        className={`text-sm ${line.startsWith('•')
                                            ? 'text-[#4A5C6A] dark:text-dark-text-secondary pl-2'
                                            : 'text-[#1A2832] dark:text-dark-text'
                                            }`}
                                    >
                                        {line}
                                    </p>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {/* Quick Actions */}
                <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)] dark:shadow-dark">
                    <h3 className="font-semibold text-[#1A2832] dark:text-white mb-3">Quick Actions</h3>
                    <div className="space-y-2">
                        <QuickAction
                            label="Start Focus Mode"
                            icon={Target}
                            onClick={() => navigate('/app/focus')}
                        />
                        <QuickAction
                            label="View Full Calendar"
                            icon={Calendar}
                            onClick={() => navigate('/app/calendar')}
                        />
                        <QuickAction
                            label="Optimize Schedule"
                            icon={Sparkles}
                            onClick={() => navigate('/app/schedule')}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

// Stat Badge Component
function StatBadge({ value, label, color }: { value: number; label: string; color: string }) {
    const bgColors: Record<string, string> = {
        white: 'bg-white/20',
        green: 'bg-green-500/30',
        amber: 'bg-amber-500/30',
        red: 'bg-red-500/30',
    };

    return (
        <div className={`${bgColors[color]} rounded-xl p-2 text-center`}>
            <div className="text-lg font-bold">{value}</div>
            <div className="text-xs text-white/80">{label}</div>
        </div>
    );
}

// Quick Action Component
function QuickAction({
    label,
    icon: Icon,
    onClick
}: {
    label: string;
    icon: React.ElementType;
    onClick: () => void
}) {
    return (
        <button
            onClick={onClick}
            className="w-full flex items-center justify-between p-3 bg-[#F5F8FA] dark:bg-dark-elevated hover:bg-[#D9EEF2] dark:hover:bg-dark-border rounded-xl transition-colors group"
        >
            <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-[#0D5F6B]" />
                <span className="font-medium text-[#1A2832] dark:text-dark-text group-hover:text-[#0D5F6B] dark:group-hover:text-dark-teal-light">{label}</span>
            </div>
            <ChevronRight className="w-5 h-5 text-[#7B8C9B] group-hover:text-[#0D5F6B]" />
        </button>
    );
}
